<script setup lang="ts">
import { prepareImageUpload } from "~/utils/image-upload";
import { useImageUploadPreviews } from "~/composables/useImageUploadPreviews";
import { SubmissionJob } from "~/classes/models/submission-job/submission-job.model";

const props = defineProps({
  job: {
    type: Object as PropType<SubmissionJob>,
    required: true,
  },
});

const emit = defineEmits(["close"]);

const removeFile = (fileName: string, index: number) => {
  if (isUploading.value || isPreparing.value) return;
  props.job.files = props.job.files.filter((file, i) => i !== index);
};

const errorMessage = ref("");
const isPreparing = ref(false);
const onRawFilesUploaded = async (files: File[] | CustomFile[]) => {
  if (isUploading.value || isPreparing.value) return;
  isPreparing.value = true;
  errorMessage.value = "";
  try {
    const prepared: (File | CustomFile)[] = [];
    for (const file of Array.from(files)) {
      prepared.push(file instanceof File ? await prepareImageUpload(file) : file);
    }
    props.job.files.push(...prepared);
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Could not add these photos. Please try again.";
  } finally {
    isPreparing.value = false;
  }
};
const previews = useImageUploadPreviews(computed(() => props.job.files));

const isUploading = ref(false);

const uploadFiles = async () => {
  if (isUploading.value || isPreparing.value) return;

  if (props.job.files.length == 0) {
    useBaseToast("Oops! Please add at least one file.", "error");
    return;
  }

  errorMessage.value = "";
  isUploading.value = true;

  try {
    await props.job.uploadFiles();
  } catch (error) {
    console.error(error);
    errorMessage.value = "Upload failed. Your photos are still here. Check your connection and try again.";
  } finally {
    isUploading.value = false;
  }
};

const isPicking = ref(false);
</script>

<template>
  <div class="flex flex-col h-full">
    <div
      class="px-4 py-2 mb-2 flex flex-row border-b items-center justify-between"
    >
      <h2 class="text-h4 font-bold">Images</h2>
      <BaseFileUploadButton
        :acceptedMimeTypes="['image/*']"
        :convert-google-file="true"
        :return-raw-files="true"
        @on-raw-files-uploaded="onRawFilesUploaded"
        :allow-multiple="true"
        @on-google-file-pick-start="isPicking = true"
        @on-google-file-pick-end="isPicking = false"
      >
        <template #trigger="{ isUploading: isPickerUploading }">
          <BaseOutlinedButton :show-spinner="isUploading || isPickerUploading || isPicking || isPreparing" :disabled="isUploading || isPreparing">
            <BaseIconAddPicture class="mr-1" />
            Add More Image(s)
          </BaseOutlinedButton>
        </template>
      </BaseFileUploadButton>
    </div>
    <div
      class="flex flex-col grow min-h-0 p-4 overflow-auto gap-4"
      v-auto-animate
    >
      <BaseEmptyListMessage v-if="job.files.length == 0">
        No Files Added
      </BaseEmptyListMessage>
      <div
        v-for="(preview, index) in previews"
        :key="preview.id"
        class="border rounded shadow-lg flex flex-col"
      >
        <img
          :src="preview.url"
          :alt="`Page ${index + 1}`"
          class="rounded-t"
        />
        <div class="border-t">
          <p class="px-4 pt-2 text-sm">Page {{ index + 1 }}</p>
          <BaseTextButton
            :disabled="isUploading || isPreparing"
            color="danger"
            @click="removeFile(preview.file.name, index)"
            class="rounded-none"
          >
            <span class="flex flex-row items-center gap-1">
              <BaseIconDelete />
              Remove
            </span>
          </BaseTextButton>
        </div>
      </div>
    </div>
    <div class="border-t p-4">
      <p v-if="errorMessage" role="alert" class="mb-3 text-danger-default">{{ errorMessage }}</p>
      <span class="text-body-2 text-secondary-default mb-2 text-center block">
        If this assignment includes more than one image, add all images before
        you click Upload.
      </span>
      <BaseButton @click="uploadFiles" :show-spinner="isUploading || isPreparing" :disabled="isUploading || isPreparing || isPicking">
        Upload
      </BaseButton>
    </div>
  </div>
</template>

<style scoped></style>
